import { cx } from '@vizit/theme';
import * as React from 'react';
import { Button } from 'react-aria-components';
import { Image } from '../image';
import { Text, TextColor, TextSize, TextWeight } from '../text';
import type { CategoryCardProps } from './types';

/**
 * Interactive card for browsing into a category: a fluid pastel tile with a
 * centered product shot spanning its full width, and the category name below.
 *
 * @example
 * <CategoryCard
 *   title="Sports & Outdoors"
 *   backgroundColor="#FEF8B9"
 *   images={[{ url: '/soccer-ball.png', alt: 'Soccer ball' }]}
 *   onPress={() => console.log('Category clicked')}
 * />
 */
function CategoryCard(props: CategoryCardProps, ref: React.ForwardedRef<HTMLButtonElement>) {
  const { title, images, backgroundColor, className, onPress } = props;

  const image = images?.[0];

  return (
    <Button
      ref={ref}
      onPress={onPress}
      className={cx(
        'group flex w-full flex-col items-start gap-3 text-left',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2',
        className,
      )}
    >
      <div
        className={cx(
          'relative flex aspect-[60/39] w-full items-center justify-center overflow-hidden',
          'after:absolute after:inset-0 after:bg-black/10 after:opacity-0 group-hover:after:opacity-100',
          'after:transition-opacity after:duration-150 motion-reduce:after:transition-none',
        )}
        style={{ backgroundColor }}
      >
        {image && (
          <Image
            border={false}
            name={image.alt || title}
            src={image.url}
            className="bg-transparent"
            imageClassName="bg-transparent"
          />
        )}
      </div>
      <Text
        size={TextSize.Md}
        weight={TextWeight.Semibold}
        color={TextColor.Tertiary}
        className="transition-colors duration-150 motion-reduce:transition-none group-hover:text-primary"
      >
        {title}
      </Text>
    </Button>
  );
}

const _CategoryCard = React.forwardRef<HTMLButtonElement, CategoryCardProps>(CategoryCard);

export { _CategoryCard as CategoryCard };
